import { computed, ref } from "vue";
import { useDebounceFn } from "@vueuse/core";
import { campaignsApi } from "@/modules/communications/infrastructure/api/campaigns-api";
import { CampaignStatus } from "@/modules/communications/domain/types/campaign";
import { useAppToast } from "@/shared/composables/useAppToast";
import { logger } from "@/infrastructure/services/LoggerService";
const PAGE_SIZE = 50;
// All-results browser cap, shared by the "see all results" modal AND the `load({ wide: true })`
// path (donations list quick filter): neither renders a virtualized list, so bound the fetch +
// render at a realistic ceiling (mirrors the create modal). Past this cap the step-2 search in the
// filter selector only covers the campaigns already loaded — a match beyond the cap won't show.
const ALL_RESULTS_PAGE_SIZE = 100;
const ALL_RESULTS_MAX = 500;
/**
 * Campaign-picker data source, shared by two call sites with different status scopes:
 * - the donation detail tab's edit pickers, where only a running campaign is attachable to a
 *   donation, so the browse list defaults to Active (a donation already linked to a now-inactive
 *   campaign keeps rendering via the seeded option, so scoping the browse list never hides the
 *   current selection);
 * - the donations list "Filtrer" dropdown (`status: null`), which must let a user pick a
 *   completed/archived campaign to see its past donations — Active-only would make that
 *   impossible for anything but an already-seeded deep-linked id.
 *
 * Owns the option list, loading flag, total count (drives the "see all results" footer) and the
 * label formatting. Mirrors {@link useSubscriptionOptions}; the current selection is seeded from a
 * known label (the detail already carries `campaignName`) instead of a by-id fetch, since the
 * campaigns endpoint has no single-item read.
 */
export function useCampaignOptions(opts = {}) {
    const statusScope = opts.status === undefined ? CampaignStatus.Active : (opts.status ?? undefined);
    const { toastError } = useAppToast();
    const campaigns = ref([]);
    const options = ref([]);
    const loading = ref(false);
    const totalCount = ref(0);
    // Server-side total for the current typed query (see `seeAllCount`).
    const searchTotal = ref(0);
    let loaded = false;
    function toOption(c) {
        return { value: c.id, label: c.name || c.id };
    }
    /** Ensures an option is present in the dropdown buffer (preselected / confirmed-from-see-all picks). */
    function prepend(option) {
        if (option.value && !options.value.some((o) => o.value === option.value)) {
            options.value = [option, ...options.value];
        }
    }
    function seedSelected(id, label) {
        if (!id || options.value.some((o) => o.value === id))
            return;
        options.value = [{ value: id, label: label?.trim() || id }, ...options.value];
    }
    /**
     * Pages through `getCampaigns` (pages of {@link ALL_RESULTS_PAGE_SIZE}) until either the server's
     * total or {@link ALL_RESULTS_MAX} is reached, whichever comes first. The query — when present —
     * is forwarded to the server, so the pages are already filtered and the cap only bounds the
     * empty-query browse. Shared by the wide `load()` path, {@link fetchAll} and the create modals'
     * "see all results" browsers (which also render the returned `totalCount` in their title).
     */
    async function fetchAllItems(query = "") {
        const search = query.trim() || undefined;
        const all = [];
        let page = 1;
        let total = Infinity;
        while (all.length < total && all.length < ALL_RESULTS_MAX) {
            const res = await campaignsApi.getCampaigns({
                page,
                pageSize: ALL_RESULTS_PAGE_SIZE,
                status: statusScope,
                search,
            });
            all.push(...res.items);
            total = res.totalCount;
            if (res.items.length === 0)
                break;
            page++;
        }
        return { items: all.slice(0, ALL_RESULTS_MAX), totalCount: total };
    }
    async function load(params = {}) {
        if (loaded && !params.force) {
            seedSelected(params.ensureSelectedId, params.ensureSelectedLabel);
            return;
        }
        // A fresh buffer resets the search state: any in-flight search result must not
        // overwrite the new buffer, and the footer count reverts to browse mode.
        searchSeq++;
        searchQuery.value = "";
        loading.value = true;
        try {
            if (params.wide) {
                const { items, totalCount: total } = await fetchAllItems();
                campaigns.value = items;
                totalCount.value = total;
                options.value = items.map(toOption);
            }
            else {
                const res = await campaignsApi.getCampaigns({
                    page: 1,
                    pageSize: PAGE_SIZE,
                    status: statusScope,
                });
                campaigns.value = res.items;
                totalCount.value = res.totalCount;
                options.value = res.items.map(toOption);
            }
            seedSelected(params.ensureSelectedId, params.ensureSelectedLabel);
            loaded = true;
        }
        catch (err) {
            logger.error("useCampaignOptions: failed to load campaigns", err);
            toastError("campaigns.loadError");
        }
        finally {
            loading.value = false;
        }
    }
    // ── Server-side search per keystroke ──────────────────────────────────────────
    // The browse buffer only carries the first page, so an in-memory filter cannot
    // reach a campaign beyond it on a large tenant. Typed queries go to
    // GET /campaigns?search= (status-scoped like every other call), debounced, with
    // a monotonic token discarding out-of-order responses.
    // Live search term, mirrored synchronously from `search` so `seeAllCount` is
    // query-aware and a query superseded during the debounce window is detected
    // before its fetch fires.
    const searchQuery = ref("");
    let searchSeq = 0;
    // Raw rows behind the current server-searched options — `noteSelected` seeds a
    // pick from them into the `campaigns` buffer.
    let searchResults = [];
    const debouncedSearchFetch = useDebounceFn(async (query) => {
        // Superseded while debouncing (newer keystroke, or query emptied) → the
        // pending fetch for the stale term must not fire at all.
        if (query !== searchQuery.value)
            return;
        const requestId = ++searchSeq;
        loading.value = true;
        try {
            const res = await campaignsApi.getCampaigns({
                search: query,
                page: 1,
                pageSize: PAGE_SIZE,
                status: statusScope,
            });
            if (requestId !== searchSeq)
                return;
            searchResults = res.items;
            options.value = res.items.map(toOption);
            searchTotal.value = res.totalCount;
        }
        catch (err) {
            if (requestId === searchSeq) {
                logger.error("useCampaignOptions: campaign search failed", err);
                toastError("campaigns.loadError");
            }
        }
        finally {
            if (requestId === searchSeq)
                loading.value = false;
        }
    }, 300);
    function search(query) {
        const q = query.trim();
        searchQuery.value = q;
        if (!q) {
            // Collapsed query: nothing left to search, so restore the browse buffer
            // synchronously (no debounce) and invalidate any in-flight response.
            searchSeq++;
            loading.value = false;
            options.value = campaigns.value.map(toOption);
            return;
        }
        void debouncedSearchFetch(q);
    }
    /** "See all results (N)" count: server total for the current query, DB total when browsing. */
    const seeAllCount = computed(() => searchQuery.value ? searchTotal.value : totalCount.value);
    /**
     * Seeds a picked server-searched row into the `campaigns` buffer. Consumers that resolve the raw
     * row by id at submit time (the create modals' campaignId/campaignName payload) call this when the
     * picker's modelValue is set — without it a selection made from search results is silently absent
     * from the buffer.
     */
    function noteSelected(id) {
        const selected = searchResults.find((c) => c.id === id);
        if (selected && !campaigns.value.some((c) => c.id === selected.id)) {
            campaigns.value = [selected, ...campaigns.value];
        }
    }
    /**
     * Fetches the "see all results" slice as options — server-filtered by the query (see
     * {@link fetchAllItems}). Bounded by {@link ALL_RESULTS_MAX} so an unvirtualized modal never
     * renders an unbounded list.
     */
    async function fetchAll(query) {
        const { items } = await fetchAllItems(query);
        return items.map(toOption);
    }
    return {
        campaigns,
        options,
        loading,
        totalCount,
        searchTotal,
        seeAllCount,
        toOption,
        load,
        search,
        noteSelected,
        fetchAll,
        fetchAllItems,
        prepend,
    };
}
